import { useTexture } from "@react-three/drei";
import { extend, useFrame } from "@react-three/fiber";
import { useMemo, useRef, forwardRef, useEffect } from "react";
import * as THREE from "three/webgpu";
import * as TSL from "three/tsl";
import { easing } from "maath";

// TSL-based clouds material using real textures like the original
// English comments only inside code blocks as requested
const CloudsTSLMaterial = class extends THREE.MeshStandardNodeMaterial {
  constructor() {
    super();

    // Initialize TSL uniforms
    this.uTime = TSL.uniform(0);
    this.uRatio = TSL.uniform(1.0);
    this.uSpeed = TSL.uniform(1.0);
    this.uCloudDensity = TSL.uniform(1.0);
    this.uNoiseScale = TSL.uniform(1.0);
    this.uDistortionStrength = TSL.uniform(1.0);
    this.uDetailAmount = TSL.uniform(1.0);
    this.uSmoothness = TSL.uniform(1.0);
    this.uContrast = TSL.uniform(1.0);
    this.uOpacity = TSL.uniform(1.0);
    this.uMouseDistortion = TSL.uniform(0.03);
    this.uMouseErase = TSL.uniform(0.3);
    this.uWindDirection = TSL.uniform(new THREE.Vector2(1.0, 0.0));
    this.uCloudDarkColor = TSL.uniform(new THREE.Color(0.737, 0.773, 0.8));
    this.uCloudLightColor = TSL.uniform(new THREE.Color(0.961, 0.969, 0.976));
    this.uDarkMultiplier = TSL.uniform(new THREE.Vector3(0.6, 0.65, 0.7));
    this.uLightMultiplier = TSL.uniform(new THREE.Vector3(0.9, 0.92, 0.95));
    this.uDensityDarkening = TSL.uniform(new THREE.Vector3(0.5, 0.6, 0.7));
    this.uDensityStrength = TSL.uniform(0.4);
    this.uInstanceSeed = TSL.uniform(0.0);

    // Set basic material properties
    this.transparent = true;
    this.side = THREE.FrontSide;
    this.alphaTest = 0.02; // Discard fragments with alpha < 0.001
    this.depthWrite = false; // Don't write to depth buffer for transparency

    // Initialize with basic white color
    this.colorNode = TSL.vec3(1.0, 1.0, 1.0);
    this.alphaNode = TSL.float(1.0);

    // Textures will be set externally
    this.tPerlin = null;
    this.tNoise = null;
    this.tMouse = null;
  }

  // Method to set up the shader nodes with loaded textures - matching original GLSL shader
  setupTSLNodes(noiseTexture, perlinTexture, mouseTexture) {
    // Create texture nodes using TSL.texture()
    const tNoise = TSL.texture(noiseTexture);
    const tPerlin = TSL.texture(perlinTexture);
    const tMouse = TSL.texture(mouseTexture);

    // Get UV coordinates
    const uv = TSL.uv();

    // Time with speed multiplier
    const time = TSL.mul(this.uTime, TSL.mul(0.5, this.uSpeed));

    // Create instance-based seed variation
    const position = TSL.positionLocal;
    const instanceSeed = TSL.add(
      TSL.mul(TSL.add(TSL.add(position.x, position.y), position.z), 0.1),
      this.uInstanceSeed
    );

    // Mouse interaction - sample mouse texture with organic noise distortion
    // Use UV coordinates for mouse sampling since fragCoord is not available in TSL
    // Map UV coordinates to screen space for proper mouse interaction
    const mouseUV = TSL.add(
      uv,
      TSL.mul(0.1, TSL.sub(tNoise.sample(TSL.mul(4.0, uv)).g, 0.5))
    );
    const mouse = TSL.clamp(tMouse.sample(mouseUV).r, 0.0, 1.0);

    // Distorted UV with mouse interaction
    let dUv = TSL.add(uv, TSL.mul(this.uMouseDistortion, mouse));
    dUv = TSL.mul(dUv, TSL.add(1.0, TSL.mul(0.025, mouse)));

    // Wind movement vectors
    const windMovement1 = TSL.mul(
      this.uWindDirection,
      TSL.vec2(-0.004, -0.025)
    );
    const windMovement2 = TSL.mul(this.uWindDirection, TSL.vec2(0.005, 0.01));
    const windMovement3 = TSL.mul(this.uWindDirection, -0.016);

    // Noise UV coordinates with wind movement and instance variation
    const noiseUv1 = TSL.add(
      TSL.add(
        TSL.mul(uv, TSL.mul(0.2, this.uNoiseScale)),
        TSL.mul(windMovement1, time)
      ),
      TSL.mul(instanceSeed, 0.1)
    );
    const noiseUv2 = TSL.add(
      TSL.add(
        TSL.mul(uv, TSL.mul(0.08, this.uNoiseScale)),
        TSL.mul(windMovement2, time)
      ),
      TSL.mul(instanceSeed, 0.05)
    );
    const perlinUv = TSL.add(
      TSL.add(
        TSL.mul(uv, TSL.mul(0.5, this.uNoiseScale)),
        TSL.mul(windMovement3, time)
      ),
      TSL.mul(instanceSeed, 0.02)
    );

    // Apply texture-based noise distortion
    const noise1 = TSL.sub(tNoise.sample(noiseUv1).r, 0.5);
    const noise2 = TSL.sub(tNoise.sample(noiseUv2).r, 0.5);
    const perlinNoise = TSL.sub(tPerlin.sample(perlinUv).r, 0.5);

    dUv = TSL.add(
      dUv,
      TSL.vec2(0.0, TSL.mul(0.3, TSL.mul(this.uDistortionStrength, noise1)))
    );
    dUv = TSL.sub(
      dUv,
      TSL.vec2(0.0, TSL.mul(0.5, TSL.mul(this.uDistortionStrength, noise2)))
    );
    dUv = TSL.mul(
      dUv,
      TSL.add(1.0, TSL.mul(0.1, TSL.mul(this.uDistortionStrength, perlinNoise)))
    );

    // Smoothness calculation
    const smoothnessWindMovement = TSL.mul(
      this.uWindDirection,
      TSL.vec2(-0.08, -0.04)
    );
    const smoothnessUV = TSL.add(
      TSL.mul(uv, TSL.mul(0.08, this.uNoiseScale)),
      TSL.mul(smoothnessWindMovement, time)
    );
    const smoothnessNoise = tNoise.sample(smoothnessUV).r;
    const smoothness = TSL.mul(
      TSL.smoothstep(0.4, 0.7, smoothnessNoise),
      this.uSmoothness
    );

    // Main cloud layer - this creates the visible clouds
    const cloudThreshold = TSL.sub(0.9, TSL.mul(0.1, smoothness));
    let clouds = TSL.mul(
      TSL.smoothstep(cloudThreshold, 0.7, dUv.y),
      this.uCloudDensity
    );

    // Additional cloud shaping
    const cloudShape = TSL.sub(
      dUv.y,
      TSL.mul(0.2, TSL.smoothstep(0.4, 1.0, dUv.x))
    );
    clouds = TSL.mul(clouds, TSL.smoothstep(0.0, 0.2, cloudShape));

    // Secondary sparse layer
    const sparseWindMovement = TSL.mul(
      this.uWindDirection,
      TSL.vec2(0.0, TSL.mul(-0.3, TSL.mul(time, 0.1)))
    );
    const sparseUV = TSL.add(
      TSL.add(TSL.mul(uv, TSL.mul(0.7, this.uNoiseScale)), sparseWindMovement),
      TSL.mul(instanceSeed, 0.03)
    );
    const sparseUVOffset = TSL.mul(
      tNoise.sample(TSL.mul(uv, TSL.mul(0.5, this.uNoiseScale))).rg,
      0.04
    );
    const sparseUVFinal = TSL.add(sparseUV, sparseUVOffset);
    const sparseNoise = tNoise.sample(sparseUVFinal).r;
    const sparseMultiplier = TSL.add(
      1.0,
      TSL.mul(0.1, tNoise.sample(TSL.mul(uv, TSL.mul(1.2, this.uNoiseScale))).r)
    );
    const sparse = TSL.mul(sparseNoise, sparseMultiplier);
    const sparseLayer = TSL.mul(
      TSL.mul(0.4, TSL.smoothstep(0.45, 0.75, sparse)),
      this.uCloudDensity
    );

    // Fine details layer
    const fluffWindMovement = TSL.mul(this.uWindDirection, TSL.mul(time, 0.3));
    const fluffUV = TSL.add(
      TSL.add(
        TSL.add(
          TSL.mul(TSL.vec2(1.2, 0.6), TSL.mul(uv, this.uNoiseScale)),
          TSL.mul(0.02, tPerlin.sample(uv).rg)
        ),
        TSL.mul(
          0.04,
          tNoise.sample(TSL.mul(uv, TSL.mul(0.8, this.uNoiseScale))).rg
        )
      ),
      TSL.add(fluffWindMovement, TSL.mul(instanceSeed, 0.08))
    );
    const fluff = tNoise.sample(fluffUV).r;
    const detailLayer = TSL.mul(
      TSL.mul(0.3, TSL.smoothstep(0.4, 0.7, fluff)),
      this.uDetailAmount
    );

    // Combine all cloud layers
    clouds = TSL.add(TSL.add(clouds, sparseLayer), detailLayer);

    // Apply mouse erasing effect
    const mouseEraseFactor = TSL.smoothstep(0.05, 0.8, mouse);
    clouds = TSL.mul(
      clouds,
      TSL.sub(1.0, TSL.mul(this.uMouseErase, mouseEraseFactor))
    );

    // Apply contrast enhancement
    clouds = TSL.smoothstep(0.2, 0.9, TSL.mul(clouds, this.uContrast));
    clouds = TSL.min(clouds, 1.0);

    // Alpha calculation - COMMENTED OUT TO DEBUG RECTANGLE
    // const edgeFadeY1 = TSL.smoothstep(1.0, 0.9, uv.y);
    // const edgeFadeY2 = TSL.smoothstep(0.0, 0.1, uv.y);
    // const edgeFadeX1 = TSL.smoothstep(0.0, 0.1, uv.x);
    // const edgeFadeX2 = TSL.smoothstep(1.0, 0.9, uv.x);
    // let alpha = TSL.mul(
    //   TSL.mul(TSL.mul(TSL.mul(clouds, edgeFadeY1), edgeFadeY2), edgeFadeX1),
    //   edgeFadeX2
    // );

    // // Additional alpha layer for more coverage - matching original
    // const additionalAlphaY1 = TSL.smoothstep(0.2, 0.3, uv.y);
    // const additionalAlphaY2 = TSL.smoothstep(0.7, 0.6, uv.y);
    // const additionalAlphaX1 = TSL.smoothstep(0.2, 0.3, uv.x);
    // const additionalAlphaX2 = TSL.smoothstep(0.9, 0.8, uv.x);
    // const additionalAlpha = TSL.mul(
    //   TSL.mul(TSL.mul(additionalAlphaY1, additionalAlphaY2), additionalAlphaX1),
    //   additionalAlphaX2
    // );

    // alpha = TSL.mul(
    //   TSL.min(TSL.add(alpha, additionalAlpha), 1.0),
    //   this.uOpacity
    // );

    // Alpha with softer edge fading to avoid rectangle
    const edgeFadeY1 = TSL.smoothstep(1.0, 0.95, uv.y);
    const edgeFadeY2 = TSL.smoothstep(0.0, 0.05, uv.y);
    const edgeFadeX1 = TSL.smoothstep(0.0, 0.05, uv.x);
    const edgeFadeX2 = TSL.smoothstep(1.0, 0.95, uv.x);
    let alpha = TSL.mul(
      TSL.mul(TSL.mul(TSL.mul(clouds, edgeFadeY1), edgeFadeY2), edgeFadeX1),
      edgeFadeX2
    );

    // Ensure minimum alpha to prevent invisible clouds
    alpha = TSL.max(alpha, TSL.mul(clouds, 0.1)); // Minimum 10% of cloud density
    alpha = TSL.mul(alpha, this.uOpacity);

    // Final safety check - ensure alpha is never completely zero
    alpha = TSL.max(alpha, 0.01);

    // Color calculation with proper density variation - matching original
    const cloudDarkness = TSL.add(
      TSL.smoothstep(0.4, 1.0, dUv.y),
      TSL.smoothstep(0.4, 0.0, dUv.y)
    );

    // Density-based color variation
    const densityVariation = TSL.add(TSL.mul(clouds, 0.7), 0.3);
    const darkColor = TSL.mul(this.uCloudDarkColor, this.uDarkMultiplier);
    const lightColor = TSL.mul(this.uCloudLightColor, this.uLightMultiplier);

    // Mix colors based on cloud density and height
    let color = TSL.mix(
      darkColor,
      lightColor,
      TSL.mul(densityVariation, cloudDarkness)
    );

    // Add depth-based darkening for denser areas
    const densityDarkening = TSL.mul(clouds, this.uDensityStrength);
    color = TSL.mix(
      color,
      TSL.mul(color, this.uDensityDarkening),
      densityDarkening
    );

    // Godrays effect using textures
    const godrayWindMovement1 = TSL.mul(
      this.uWindDirection,
      TSL.vec2(TSL.mul(-0.005, this.uTime), 0.0)
    );
    const godrayWindMovement2 = TSL.mul(
      this.uWindDirection,
      TSL.vec2(TSL.mul(0.005, this.uTime), 0.0)
    );
    const godrayUV1 = TSL.add(
      TSL.mul(TSL.vec2(4.0, 0.4), uv),
      godrayWindMovement1
    );
    const godrayUV2 = TSL.add(
      TSL.mul(TSL.vec2(4.0, 0.01), uv),
      godrayWindMovement2
    );

    let godrays = tNoise.sample(godrayUV1).r;
    godrays = TSL.mul(godrays, TSL.smoothstep(0.81, 1.0, godrays));
    godrays = TSL.mul(
      godrays,
      TSL.smoothstep(0.45, 0.5, tNoise.sample(godrayUV2).r)
    );
    godrays = TSL.mul(godrays, TSL.smoothstep(0.0, 0.5, uv.y));

    // Mix godrays into color
    const godrayColor = TSL.vec3(0.95, 1.0, 0.9);
    color = TSL.mix(color, godrayColor, TSL.smoothstep(0.001, 0.0, alpha));
    alpha = TSL.add(alpha, TSL.mul(2.0, TSL.mul(godrays, TSL.sub(1.0, alpha))));

    // Create vec4 with color and alpha - following modern TSL pattern
    // const finalColorWithAlpha = TSL.vec4(color, 1.0);
    const finalColorWithAlpha = TSL.vec4(color, alpha);

    // Set the material properties
    this.colorNode = finalColorWithAlpha;
    this.transparent = true;
    this.side = THREE.FrontSide;

    // Mark that shader nodes have been created
    this.shaderNodesCreated = true;
  }

  // Fallback method to create basic shader without textures
  setupBasicShader() {
    // console.log("CloudsTSL: Creating fallback basic shader");

    // Get UV coordinates
    const uv = TSL.uv();

    // Time with speed multiplier
    const time = TSL.mul(this.uTime, TSL.mul(0.5, this.uSpeed));

    // Create instance-based seed variation
    const position = TSL.positionLocal;
    const instanceSeed = TSL.add(
      TSL.mul(TSL.add(TSL.add(position.x, position.y), position.z), 0.1),
      this.uInstanceSeed
    );

    // Simple procedural noise using TSL functions
    const noise1 = TSL.mul(
      TSL.sub(
        TSL.sin(
          TSL.add(
            TSL.mul(uv, TSL.mul(0.2, this.uNoiseScale)),
            TSL.mul(time, 0.1)
          )
        ),
        0.5
      ),
      0.1
    );
    const noise2 = TSL.mul(
      TSL.sub(
        TSL.sin(
          TSL.add(
            TSL.mul(uv, TSL.mul(0.08, this.uNoiseScale)),
            TSL.mul(time, 0.05)
          )
        ),
        0.5
      ),
      0.1
    );

    // Distorted UV with simple noise
    let dUv = TSL.add(uv, TSL.mul(this.uDistortionStrength, noise1));
    dUv = TSL.add(dUv, TSL.mul(this.uDistortionStrength, noise2));

    // Simple cloud generation
    let clouds = TSL.smoothstep(0.6, 0.4, dUv.y);
    clouds = TSL.mul(clouds, this.uCloudDensity);

    // Apply contrast
    clouds = TSL.smoothstep(0.2, 0.9, TSL.mul(clouds, this.uContrast));
    clouds = TSL.min(clouds, 1.0);

    // Simple alpha with edge fading
    const edgeFadeY1 = TSL.smoothstep(1.0, 0.95, uv.y);
    const edgeFadeY2 = TSL.smoothstep(0.0, 0.05, uv.y);
    const edgeFadeX1 = TSL.smoothstep(0.0, 0.05, uv.x);
    const edgeFadeX2 = TSL.smoothstep(1.0, 0.95, uv.x);
    let alpha = TSL.mul(
      TSL.mul(TSL.mul(TSL.mul(clouds, edgeFadeY1), edgeFadeY2), edgeFadeX1),
      edgeFadeX2
    );

    // Ensure minimum alpha to prevent invisible clouds
    alpha = TSL.max(alpha, TSL.mul(clouds, 0.1)); // Minimum 10% of cloud density
    alpha = TSL.mul(alpha, this.uOpacity);

    // Final safety check - ensure alpha is never completely zero
    alpha = TSL.max(alpha, 0.01);

    // Simple color
    const darkColor = TSL.mul(this.uCloudDarkColor, this.uDarkMultiplier);
    const lightColor = TSL.mul(this.uCloudLightColor, this.uLightMultiplier);
    const color = TSL.mix(darkColor, lightColor, clouds);

    // Create vec4 with color and alpha
    const finalColorWithAlpha = TSL.vec4(color, alpha);

    // Set the material properties
    this.colorNode = finalColorWithAlpha;
    this.transparent = true;
    this.side = THREE.FrontSide;

    // Mark that fallback shader has been created
    this.fallbackShaderCreated = true;
    // console.log("CloudsTSL: Fallback shader created successfully");
  }
};

extend({ CloudsTSLMaterial });

const CloudsTSL = forwardRef(function CloudsTSL(
  {
    size = [1, 1],
    seed,
    // Animation controls
    speed = 1.0,
    // Wind direction control
    windDirection = [1.0, 0.0],
    // Visual controls
    cloudDensity = 1.0,
    noiseScale = 1.0,
    distortionStrength = 1.0,
    detailAmount = 1.0,
    smoothness = 1.0,
    contrast = 1.0,
    opacity = 1.0,
    // Mouse interaction controls
    mouseFade = 0.001,
    mouseRadius = 60,
    mouseDistortion = 0.03,
    mouseErase = 0.3,
    // Color controls
    cloudDarkColor,
    cloudLightColor,
    darkMultiplier,
    lightMultiplier,
    densityDarkening,
    densityStrength,
    depthTest = false,
    depthWrite = false,
    type = "plane",
    renderOrder = 100,
    ...props
  },
  meshRef
) {
  const ref = useRef();
  const internalMeshRef = useRef();

  // Generate unique seed for each instance if not provided
  const instanceSeed = useMemo(() => {
    if (seed !== undefined) return seed;
    return Math.random() * 1000;
  }, [seed]);

  // Load textures from public folder
  const [noiseTex, perlinTex] = useTexture([
    "/custom_noise.webp",
    "/custom_perlin.webp",
  ]);

  // Create mouse texture for interaction
  const mouseTexture = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 256;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, 256, 256);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.needsUpdate = true;
    return texture;
  }, []);

  // Configure texture sampling and wrapping
  useMemo(() => {
    if (!noiseTex || !perlinTex) return;
    noiseTex.wrapS = noiseTex.wrapT = THREE.RepeatWrapping;
    perlinTex.wrapS = perlinTex.wrapT = THREE.RepeatWrapping;
    const Linear = THREE.LinearSRGBColorSpace || THREE.LinearEncoding;
    if ("colorSpace" in noiseTex) {
      noiseTex.colorSpace = Linear;
      perlinTex.colorSpace = Linear;
    } else if ("encoding" in noiseTex) {
      noiseTex.encoding = THREE.LinearEncoding;
      perlinTex.encoding = THREE.LinearEncoding;
    }
    noiseTex.needsUpdate = true;
    perlinTex.needsUpdate = true;
  }, [noiseTex, perlinTex]);

  // Update TSL texture uniforms when textures are loaded - following Three.js WebGPU example pattern
  useEffect(() => {
    if (ref.current && noiseTex && perlinTex && mouseTexture) {
      try {
        // Call setupTSLNodes with loaded textures - following the example pattern
        ref.current.setupTSLNodes(noiseTex, perlinTex, mouseTexture);
        // console.log("CloudsTSL: Shader nodes created successfully");
      } catch (error) {
        // console.error("TSL: Error creating shader nodes:", error);
        // Fallback: try to create basic shader without textures
        try {
          ref.current.setupBasicShader();
          // console.log("CloudsTSL: Fallback basic shader created");
        } catch (fallbackError) {
          // console.error("TSL: Fallback shader also failed:", fallbackError);
        }
      }
    } else {
      // console.warn("CloudsTSL: Textures not ready yet", {
      //   noiseTex: !!noiseTex,
      //   perlinTex: !!perlinTex,
      //   mouseTexture: !!mouseTexture,
      //   material: !!ref.current,
      // });
    }
  }, [noiseTex, perlinTex, mouseTexture]);

  // Fallback timeout to ensure shader is created even if textures fail to load
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        ref.current &&
        !ref.current.shaderNodesCreated &&
        !ref.current.fallbackShaderCreated
      ) {
        // console.warn(
        //   "CloudsTSL: Textures took too long to load, creating fallback shader"
        // );
        try {
          ref.current.setupBasicShader();
          // console.log("CloudsTSL: Timeout fallback shader created");
        } catch (error) {
          // console.error("TSL: Timeout fallback shader failed:", error);
        }
      }
    }, 3000); // 3 second timeout

    return () => clearTimeout(timeout);
  }, []);

  // Mouse interaction state with mesh-based coordinates
  const mouse = useRef(new THREE.Vector2(0.5, 0.5));
  const mouseData = useRef({
    x: 0.5,
    y: 0.5,
    prevX: 0.5,
    prevY: 0.5,
    vx: 0,
    vy: 0,
    speed: 0,
    isActive: false,
    hasPrev: false,
  });
  const mouseSpeed = useRef(0);

  // Raycaster for mesh intersection
  const raycaster = useRef(new THREE.Raycaster());
  const mouseVector = useRef(new THREE.Vector2());

  // Function to update mouse texture with accumulative trails - matching original
  const updateMouseTexture = (delta) => {
    if (!mouseTexture) return;

    const canvas = mouseTexture.image;
    const ctx = canvas.getContext("2d");

    // Fade existing content more gradually for smoother trails
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = `rgba(0, 0, 0, ${Math.max(0, Math.min(0.15, mouseFade))})`;
    ctx.fillRect(0, 0, 256, 256);

    // Draw new mouse position if active
    if (mouseData.current.isActive) {
      ctx.globalCompositeOperation = "lighter";

      // Convert normalized mouse coordinates to texture coordinates
      // Ensure proper screen space mapping
      const cx = Math.max(0, Math.min(255, mouseData.current.x * 256));
      const cy = Math.max(0, Math.min(255, (1 - mouseData.current.y) * 256));
      const prx = Math.max(
        0,
        Math.min(255, (mouseData.current.prevX ?? mouseData.current.x) * 256)
      );
      const pry = Math.max(
        0,
        Math.min(
          255,
          (1 - (mouseData.current.prevY ?? mouseData.current.y)) * 256
        )
      );
      const vx = cx - prx;
      const vy = cy - pry;
      const angle = Math.atan2(vy, vx);

      // Radius grows with speed
      const speedNorm = Math.max(0, Math.min(1, mouseSpeed.current * 0.5));
      const r = Math.max(3, mouseRadius * (0.6 + 1.6 * speedNorm));

      // Soft trail between previous and current positions
      if (mouseData.current.hasPrev) {
        ctx.strokeStyle = "rgba(255, 255, 255, 0.12)";
        ctx.lineWidth = r * (0.3 + 0.4 * speedNorm);
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(prx, pry);
        ctx.lineTo(cx, cy);
        ctx.stroke();
      }

      // Elliptical head oriented to movement to avoid perfect circle
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(angle);
      const rx = r * (0.7 + 0.6 * speedNorm);
      const ry = Math.max(3, r * (0.45 + 0.2 * speedNorm));
      // Very soft gradient to avoid hard circular edges
      const grad = ctx.createRadialGradient(
        0,
        0,
        0, // Start from center
        0,
        0,
        Math.max(rx, ry) * 1.8 // Much larger radius for very soft edges
      );
      grad.addColorStop(0, "rgba(255,255,255,0.08)"); // Very low center opacity
      grad.addColorStop(0.4, "rgba(255,255,255,0.04)"); // Gradual falloff
      grad.addColorStop(0.8, "rgba(255,255,255,0.01)"); // Very gradual falloff
      grad.addColorStop(1, "rgba(255,255,255,0.0)"); // Complete transparency
      ctx.fillStyle = grad;
      ctx.beginPath();
      if (ctx.ellipse) {
        ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
      } else {
        ctx.arc(0, 0, Math.max(rx, ry), 0, Math.PI * 2);
      }
      ctx.fill();
      ctx.restore();

      // Minimal noise dots for subtle organic feel
      for (let i = 0; i < 2; i++) {
        const ang = Math.random() * Math.PI * 2;
        const rad = r * (0.3 + Math.random() * 0.4);
        const sx = cx + Math.cos(ang) * rad * 0.15;
        const sy = cy + Math.sin(ang) * rad * 0.15;
        const dotGrad = ctx.createRadialGradient(sx, sy, 0, sx, sy, r * 0.2);
        dotGrad.addColorStop(0, "rgba(255,255,255,0.06)");
        dotGrad.addColorStop(1, "rgba(255,255,255,0.0)");
        ctx.beginPath();
        ctx.arc(sx, sy, r * 0.2, 0, Math.PI * 2);
        ctx.fillStyle = dotGrad;
        ctx.fill();
      }

      // Update previous position
      mouseData.current.prevX = mouseData.current.x;
      mouseData.current.prevY = mouseData.current.y;
      mouseData.current.hasPrev = true;
    }

    mouseTexture.needsUpdate = true;
  };

  useFrame((state, delta) => {
    const t = state.clock.getElapsedTime();
    if (ref.current && internalMeshRef.current) {
      // Ensure shader is created - fallback if needed
      if (
        !ref.current.shaderNodesCreated &&
        !ref.current.fallbackShaderCreated
      ) {
        // console.warn("CloudsTSL: No shader created yet, attempting fallback");
        try {
          ref.current.setupBasicShader();
        } catch (error) {
          // console.error(
          //   "CloudsTSL: Failed to create fallback shader in useFrame:",
          //   error
          // );
        }
      }

      ref.current.uTime.value = t;
      ref.current.uRatio.value = state.size.width / state.size.height;

      // Use raycasting to get UV coordinates from mesh intersection
      const globalMouse = state.pointer;
      mouseVector.current.set(globalMouse.x, globalMouse.y);

      // Update raycaster with current camera and mouse position
      raycaster.current.setFromCamera(mouseVector.current, state.camera);

      // Get intersections with the clouds mesh
      const intersects = raycaster.current.intersectObject(
        internalMeshRef.current
      );

      let normalizedMouse = { x: 0.5, y: 0.5 };
      let isIntersecting = false;

      if (intersects.length > 0) {
        const intersection = intersects[0];
        if (intersection.uv) {
          // Use UV coordinates directly from the mesh intersection
          normalizedMouse = {
            x: intersection.uv.x,
            y: intersection.uv.y,
          };
          isIntersecting = true;
        }
      }

      // Use easing.damp for smooth mouse following
      easing.damp(mouse.current, "x", normalizedMouse.x, 0.25, delta);
      easing.damp(mouse.current, "y", normalizedMouse.y, 0.25, delta);

      // Update mouse data for texture
      const prevX = mouseData.current.x;
      const prevY = mouseData.current.y;
      mouseData.current.x = mouse.current.x;
      mouseData.current.y = mouse.current.y;
      mouseData.current.isActive = isIntersecting; // Only active when intersecting mesh

      // Compute velocity and smooth speed
      const dx = mouseData.current.x - prevX;
      const dy = mouseData.current.y - prevY;
      const inst = Math.sqrt(dx * dx + dy * dy) / Math.max(1e-4, delta);
      mouseSpeed.current = 0.85 * mouseSpeed.current + 0.15 * inst;
      mouseData.current.vx = dx / Math.max(1e-4, delta);
      mouseData.current.vy = dy / Math.max(1e-4, delta);

      updateMouseTexture(delta);
    }
  });

  // Map React props to TSL uniform .value fields to avoid overwriting Node uniforms
  useEffect(() => {
    if (!ref.current) return;
    const mat = ref.current;

    // Animation and wind
    mat.uSpeed.value = speed;
    mat.uWindDirection.value = new THREE.Vector2(...windDirection);

    // Visual controls
    mat.uCloudDensity.value = cloudDensity;
    mat.uNoiseScale.value = noiseScale;
    mat.uDistortionStrength.value = distortionStrength;
    mat.uDetailAmount.value = detailAmount;
    mat.uSmoothness.value = smoothness;
    mat.uContrast.value = contrast;
    mat.uOpacity.value = opacity;

    // Mouse interaction controls
    mat.uMouseDistortion.value = mouseDistortion;
    mat.uMouseErase.value = mouseErase;

    // Color controls with fallbacks matching previous JSX defaults
    mat.uCloudDarkColor.value = cloudDarkColor
      ? new THREE.Color(cloudDarkColor)
      : new THREE.Color(0.97, 0.97, 0.97);
    mat.uCloudLightColor.value = cloudLightColor
      ? new THREE.Color(cloudLightColor)
      : new THREE.Color(1.41, 1.41, 1.41);
    mat.uDarkMultiplier.value = darkMultiplier
      ? new THREE.Vector3(...darkMultiplier)
      : new THREE.Vector3(0.86, 0.9, 0.95);
    mat.uLightMultiplier.value = lightMultiplier
      ? new THREE.Vector3(...lightMultiplier)
      : new THREE.Vector3(0.9, 0.92, 0.95);
    mat.uDensityDarkening.value = densityDarkening
      ? new THREE.Vector3(...densityDarkening)
      : new THREE.Vector3(0.5, 0.6, 0.7);
    mat.uDensityStrength.value =
      densityStrength !== undefined ? densityStrength : 0.2;

    // Instance seed
    mat.uInstanceSeed.value = instanceSeed;
  }, [speed, windDirection, cloudDensity, noiseScale, distortionStrength, detailAmount, smoothness, contrast, opacity, mouseDistortion, mouseErase, cloudDarkColor, cloudLightColor, darkMultiplier, lightMultiplier, densityDarkening, densityStrength, instanceSeed]);

  return (
    <mesh
      ref={(node) => {
        internalMeshRef.current = node;
        if (meshRef) {
          if (typeof meshRef === "function") {
            meshRef(node);
          } else {
            meshRef.current = node;
          }
        }
      }}
      {...props}
      renderOrder={renderOrder}
    >
      {type === "plane" ? (
        <planeGeometry args={[size[0], size[1], 1, 1]} />
      ) : (
        <sphereGeometry args={[size[0], size[1], 32]} />
      )}
      <cloudsTSLMaterial
        ref={ref}
        transparent={true}
        // alphaTest={0.02}
        // depthWrite={false}
        // depthTest={depthTest}
        // toneMapped={true}
        side={type === "plane" ? THREE.FrontSide : THREE.BackSide}
      />
    </mesh>
  );
});

export default CloudsTSL;
